import React from 'react'
import moment from 'moment'
import Grid from '@mui/material/Grid'
import { useTranslation } from 'react-i18next'
import { Dropdown, TextField } from '@carsys/form'
import { Alert } from '@carsys/ui'
import { useFormikContext } from 'formik'
import Typography from '@mui/material/Typography'
import useQuery from '@cbgms/base/http/use-query'
import { useDispatch } from 'react-redux'
import { testInvoiceSequencePattern } from 'app/settings/state/InvoiceSequences/actions'
import { isValidPatternLength, yearValidation } from './Validation'
import { FormValues } from './Types.d'

const numberOptions = [
  { key: 'NNNNN', value: 'NNNNN', text: '(NNNNN) - 12345' },
  { key: 'NNNNNN', value: 'NNNNNN', text: '(NNNNNN) - 123456' },
  { key: 'NNNNNNN', value: 'NNNNNNN', text: '(NNNNNNN) - 1234567' }
]

export const InvoicePatternFields: React.FC<{}> = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { values, isValid } = useFormikContext<FormValues>()

  const readOnly = !values.IsUpdatable

  const isValidLength = isValidPatternLength(values)
  const yearOptions = React.useMemo(() => {
    let date = moment()

    if (yearValidation.isValidSync(values.Year)) {
      date = date.year(values.Year)
    }

    return [
      { key: 'YY', value: 'YY', text: `(YY) - ${date.format('YY')}` },
      { key: 'YYYY', value: 'YYYY', text: `(YYYY) - ${date.format('YYYY')}` }
    ]
  }, [values.Year])

  const { data: example } = useQuery(
    ['TEST-INVOICE-PATTERN', values.PatternYear, values.PatternExtra, values.PatternNumbers, values.Year],
    async () => {
      const pattern = values.PatternYear + values.PatternExtra + values.PatternNumbers
      const { Data } = await dispatch(testInvoiceSequencePattern(pattern, values.Year))
      return Data
    },
    { cacheTime: 0, enabled: isValidLength && isValid }
  )

  return (
    <>
      <Grid item xs={4}>
        <Dropdown
          name='PatternYear'
          data-testid='PatternYear-dropdown'
          label={t('invoice_sequence.pattern_year')}
          required
          options={yearOptions}
        />
      </Grid>
      <Grid item xs={4}>
        <TextField
          name='PatternExtra'
          label={t('invoice_sequence.pattern_extra')}
          type='text'
          onKeyPress={event => {
            if (!/[0-9]/.test(event.key)) {
              event.preventDefault()
            }
          }}
          inputProps={{ readOnly, maxLength: '2' }}
        />
      </Grid>
      <Grid item xs={4}>
        <Dropdown
          name='PatternNumbers'
          data-testid='PatternNumbers-dropdown'
          label={t('invoice_sequence.pattern_numbers')}
          required
          options={numberOptions}
        />
      </Grid>

      {!isValidLength && (
        <Grid item xs={12}>
          <Alert severity='error' title={t('invoice_sequence.too_long')} />
        </Grid>
      )}

      <Grid item xs={12}>
        <Alert severity='info' title={t('invoice_sequence.example')}>
          <Typography variant='body2'>{t('invoice_sequence.invoice_number_explanation')}</Typography>
          <Typography variant='body2'>
            {t('invoice_sequence.invoice_number_example_header')} : {example}
          </Typography>
        </Alert>
      </Grid>
    </>
  )
}
